import {
  Box,
  Button,
  ButtonBase,
  Menu,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React, { useState } from "react";
import UploadRecentImages from "../UploadRecentImages";
import MyGenerationsImages from "../MyGenerationsImages";
import ExpertExStockImages from "../ExpertExStockImages";
import { FilterIcon } from "../../../../Custom-imeges/FilterIcon";
import { DateCalendar, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateIcon } from "../../../../Custom-imeges/DateIcon";
import dayjs, { Dayjs } from "dayjs";
import { tooltipStyles } from "./InputText";

enum ImageSourcePanel {
  Upload,
  MyGenerations,
  ExpertExStock,
}

const imageSourceOptions = [
  {
    title: "Upload",
    panelValue: ImageSourcePanel.Upload,
  },
  {
    title: "My generations",
    panelValue: ImageSourcePanel.MyGenerations,
  },
  {
    title: "ExpertEx stock",
    panelValue: ImageSourcePanel.ExpertExStock,
  },
];

interface ImageInputSourceSelection {
  setImageSelected: (image: File | string | null) => void;
  handleClose: () => void;
}
const ImageInputSourceSelection = ({
  setImageSelected,
  handleClose,
}: ImageInputSourceSelection) => {
  const responsiveSize = useMediaQuery("(max-width: 700px)");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [panelValue, setPanelValue] = useState<number>(ImageSourcePanel.Upload);
  const [dateValue, setDateValue] = useState<Dayjs | null>(null);
  const [dateFilter, setDateFilter] = useState<string | null>(null);
  const [imageSourceSelected, setImageSourceSelected] = useState<
    File | string | null
  >(null);
  const handleOpenFilter = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDateChange = (newValue: Dayjs | null) => {
    setDateValue(newValue);
  };

  const handleConfirmImageSelection = () => {
    if (!imageSourceSelected) {
      return;
    }
    setImageSelected(imageSourceSelected);
    setImageSourceSelected(null);
    handleClose();
  };

  const handleSaveDateFilter = () => {
    if (dateValue) {
      const utcDate = dateValue.toISOString();
      setDateFilter(utcDate);
    } else {
      setDateFilter(null);
    }
    setAnchorEl(null);
  };

  const handleResetFiltration = () => {
    setDateValue(null);
    setDateFilter(null);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        backgroundColor: "var(--sub_background6)",
        borderRadius: "0px 0px 12px 12px",
        border: "1px solid rgba(118, 207, 235, 0.20)",
        borderTop: "none",
        overflow: "hidden",
      }}
    >
      {/* source data */}

      <Box
        sx={{
          mt: 2,
          p: "0px 16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          position: "relative",
          "&::after": {
            content: '""',
            position: "absolute",
            left: 0,
            bottom: 0,
            width: "100%",
            height: "1px",
            borderRadius: "2px",
            background: "var(--selectionImageSourceHeaderBackground)",
            zIndex: 1,
          },
        }}
      >
        {/* image selection source */}
        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            gap: { xs: "5px", md: "8px" },
          }}
        >
          {imageSourceOptions.map((option) => (
            <Tooltip
              disableHoverListener={true}
              disableFocusListener={responsiveSize}
              disableTouchListener={responsiveSize}
              title= {option.title}
              key={option.panelValue}
              arrow
              placement="top"
              componentsProps={tooltipStyles}
            >
              <Typography
                onClick={() => setPanelValue(option.panelValue)}
                sx={{
                  padding: "8px",
                  color:
                    panelValue == option.panelValue
                      ? "#12B6EA"
                      : "var(--M-background117)",
                  fontFamily: "Roboto",
                  fontSize: { xs: "14px", sm: "16px" },
                  fontStyle: "normal",
                  fontWeight: 500,
                  borderBottom: `1px solid ${
                    panelValue == option.panelValue ? "#12B6EA" : "transparent"
                  }`,
                  cursor: "pointer",
                  userSelect: "none",
                  opacity: panelValue == option.panelValue ? 1 : 0.7,
                  zIndex: 2,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: {
                    xs:
                      panelValue === ImageSourcePanel.MyGenerations
                        ? "90px"
                        : "100px",
                    sm: "100%",
                  },
                }}
              >
                {option.title}
              </Typography>
            </Tooltip>
          ))}
        </Box>
        {/* filter */}
        {panelValue === ImageSourcePanel.MyGenerations && (
          <ButtonBase
            onClick={handleOpenFilter}
            sx={{
              position:"absolute",
              right: "16px",
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 100,
              width: { xs: "32px", md: "40px" },
              height: { xs: "32px", md: "40px" },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid #12B6EA",
              borderRadius: "10px",
              cursor: "pointer",
              backgroundColor: "var(--M-background126)",
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
            id="basic-button"
            aria-controls={open ? "basic-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
          >
            <FilterIcon className="custom-icon" />
          </ButtonBase>
        )}
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          sx={{
            "& .MuiPaper-root": {
              background: "var(--M-background126)",
              padding: "8px",
              color: "var(--Main_icons_color)",
              borderRadius: "8px",
              boxShadow: "0px 4px 8px 4px rgba(0, 0, 0, 0.25)",
            },
          }}
        >
          <Box className="suite-history-date-filter">
            <Box
              className="suite-history-date-filter-from"
              sx={{
                width: "100%",
              }}
            >
              <Box className="suite-history-date-from">
                <Typography>
                  {dateValue ? dateValue.format("YYYY-MM-DD") : "Date"}
                </Typography>
                <DateIcon className="custom-icon" />
              </Box>
              <Box className="suite-history-date-filter-from-date">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateCalendar
                    value={dateValue || dayjs()}
                    onChange={handleDateChange}
                    disableFuture
                    views={["day"]}
                  />
                </LocalizationProvider>
              </Box>
            </Box>
          </Box>
          <Box
            className="filter-option-save-options"
            sx={{
              gap: 2,
              px: 1,
            }}
          >
            {/* reset */}
            <Box
              className="reset-all-suite-history"
              sx={{
                width: "10%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              onClick={handleResetFiltration}
            >
              Reset
            </Box>
            {/* save and cancel */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                width: "90%",
              }}
            >
              <Box
                className="cancel-option"
                onClick={() => setAnchorEl(null)}
                sx={{
                  width: "35%",
                  height: "35px",
                }}
              >
                Cancel
              </Box>
              <Box
                className="apply-option"
                onClick={handleSaveDateFilter}
                sx={{
                  width: "35%",
                  height: "35px",
                  border: "2px solid transparent",
                }}
              >
                Save
              </Box>
            </Box>
          </Box>
        </Menu>
      </Box>
      {/* content based on selected panel */}
      <Box
        sx={{
          my: 2,
          p: "8px 16px",
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          overflow: "auto",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        {panelValue === ImageSourcePanel.Upload && (
          <UploadRecentImages setImageSourceSelected={setImageSourceSelected} />
        )}
        {panelValue === ImageSourcePanel.MyGenerations && (
          <MyGenerationsImages
            setImageSourceSelected={setImageSourceSelected}
            dateFilter={dateFilter} // Pass the date filter if needed
          />
        )}
        {panelValue === ImageSourcePanel.ExpertExStock && (
          <ExpertExStockImages
            setImageSourceSelected={setImageSourceSelected}
          />
        )}
      </Box>

      {/* buttons */}
      <Box
        sx={{
          p: "8px 16px",
          my: 1,
          display: "flex",
          justifyContent: "flex-end",
          gap: "16px",
        }}
      >
        <ButtonBase
          onClick={handleClose}
          sx={{
            p: "10px 16px",
            color: "var(--border2)",
            width: { xs: "50%", sm: "30%", md: "12%" },
            borderRadius: "10px",
            fontSize: "14px",
            fontStyle: "normal",
            fontWeight: 700,
            border: "1px solid #12B6EA",
            "&:hover": {
              backgroundColor: "rgba(118, 207, 235, 0.2)",
            },
          }}
        >
          Cancel
        </ButtonBase>
        <ButtonBase
          disabled={!imageSourceSelected}
          onClick={handleConfirmImageSelection}
          sx={{
            p: "10px 16px",
            color: "#fff",
            width: { xs: "50%", sm: "30%", md: "12%" },
            backgroundColor: "#12B6EA",
            borderRadius: "10px",
            fontSize: "14px",
            fontStyle: "normal",
            fontWeight: 700,
            border: "1px solid transparent",
            "&:hover": {
              backgroundColor: "#006874",
            },
          }}
        >
          Confirm
        </ButtonBase>
      </Box>
    </Box>
  );
};

export default ImageInputSourceSelection;
